/**
 * @village/shared — the contract between the simulation server and the web client.
 * Everything that crosses the WebSocket or lands in a snapshot file is defined
 * here. Keep it JSON-serialisable.
 *
 * Coordinates: the world is a SIZE x SIZE grid of voxel columns. `x` runs east,
 * `z` runs south, `y` is up. A villager stands on top of the column at (x, z).
 * Row-major index: idx = z * size + x.
 */
export const PLACEABLE = {
    dirt: 'dirt',
    stone: 'cobble',
    wood: 'plank',
};
/** What a fresh prop carries, and what a bush regrows back up to. */
export const PROP_MAX_HP = { tree: 4, bush: 3, rock: 5 };
export const HEIGHT_MAX = 8;
export const HEIGHT_MIN = 1;
export const WATER_LEVEL = 1;
export const RESOURCES = ['wood', 'stone', 'dirt', 'food'];
/**
 * Room to carry. Deliberately more than a hut costs: at exactly a hut's worth
 * a villager could hold the materials or its lunch, never both, and spent
 * every day dropping one for the other.
 */
export const MAX_CARRY = 16;
export const DIRS = ['north', 'east', 'south', 'west'];
export const DIR_VEC = {
    north: [0, -1],
    east: [1, 0],
    south: [0, 1],
    west: [-1, 0],
};
/** Every move option Jev can be offered. `toward_*` are one A* step computed by the server. */
export const MOVES = [
    'north',
    'east',
    'south',
    'west',
    'stay',
    'toward_home',
    'toward_tree',
    'toward_water',
    'toward_rock',
    'toward_food',
    'toward_build_site',
    /** one step towards the nearest cell nobody has stood near yet */
    'toward_unknown',
];
/** Every action Jev can be offered. The server only offers the ones possible right here. */
export const ACTIONS = [
    'none',
    'chop', // adjacent tree  → +wood
    'mine', // adjacent rock  → +stone
    'pick', // adjacent bush / crop → +food
    'dig', // here: height-1 → +dirt (or +stone if the column is stone)
    'place_dirt', // here or adjacent: height+1
    'place_stone',
    'place_wood',
    'plant', // here on dirt → crop
    'eat', // -food, hunger up
    'drink', // adjacent water
    'sleep', // energy up; much faster at home / inside a hut
    'settle', // home = here
    'build_hut',
    'build_fence',
    'build_bridge',
    'build_campfire',
    'build_path',
    'continue_building',
    'abandon_building',
    /** put one thing down to free a slot — not construction, just lightening the load */
    'drop_wood',
    'drop_stone',
    'drop_dirt',
];
export const GOALS = ['gather_wood', 'gather_stone', 'find_food', 'build', 'explore', 'rest', 'go_home'];
export const BLUEPRINT_COST = {
    hut: { wood: 8, stone: 2 },
    fence: { wood: 4 },
    bridge: { dirt: 3, wood: 1 },
    campfire: { wood: 3, stone: 1 },
    path: { stone: 1 },
};
/** Ticks from planting to a crop being worth picking, and what it yields. */
export const CROP_RIPEN_TICKS = 40;
export const CROP_YIELD = 2;
/** How many ticks an action takes before it completes. Others complete in 1. */
export const ACTION_TICKS = {
    chop: 3,
    mine: 4,
    dig: 2,
    pick: 1,
    sleep: 6,
};
// ---------------------------------------------------------------- helpers
export const idx = (size, x, z) => z * size + x;
export function phaseOf(hour) {
    if (hour >= 5 && hour < 7)
        return 'dawn';
    if (hour >= 7 && hour < 18)
        return 'day';
    if (hour >= 18 && hour < 20)
        return 'dusk';
    return 'night';
}
export const carried = (inv) => inv.wood + inv.stone + inv.dirt + inv.food;
export const emptyInventory = () => ({ wood: 0, stone: 0, dirt: 0, food: 0 });
//# sourceMappingURL=index.js.map